/**
 * Honeybadger Error Filtering Rules
 *
 * This module contains all the logic for determining which errors should be
 * reported to Honeybadger and which should be ignored. Each filter includes
 * documentation explaining why it exists and links to relevant GitHub issues.
 *
 * @memberof @albert-io/notifier
 */

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if an error notice is from an API error response
 * API errors are already handled by the application's error handling system
 */
const isApiError = (notice) => {
  const message = notice.message || '';
  return (
    message.includes('"errors":[') && message.includes('"code":') && message.includes('"status":')
  );
};

/**
 * Check if current URL path includes a given string
 */
export const isCurrentUrlMatch = (match) => {
  if (!match) return false;
  const {pathname} = window.location;
  return pathname.includes(match);
};

/**
 * Crawler UA tokens observed in project 121794's notice volume (#18857).
 * Explicit list, NOT /bot/i — real devices match the broad form (CUBOT phones).
 */
const CRAWLER_UA_PATTERN =
  /Googlebot|AdsBot|GoogleOther|Google Web Preview|Google-InspectionTool|Mediapartners-Google|bingbot|BingPreview|Applebot|meta-webindexer|facebookexternalhit|ClaudeBot|GPTBot|OAI-SearchBot|PerplexityBot|Bytespider|PetalBot|Amazonbot|DuckDuckBot|YandexBot|Baiduspider|SemrushBot|AhrefsBot|MJ12bot|DotBot|Slurp|HeadlessChrome|PhantomJS|Lightpanda/;

export const isCrawlerUserAgent = (userAgent) => CRAWLER_UA_PATTERN.test(userAgent || '');

// ============================================================================
// ERROR FILTERING RULES
// ============================================================================

/**
 * Determine if an error should be reported to Honeybadger
 *
 * @param {Object} notice - The Honeybadger notice object
 * @returns {boolean} - true to report the error, false to suppress it
 */
export const shouldReportError = (notice) => {
  const message = notice.message || '';
  const userAgent = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(userAgent);

  // Suppress ALL reporting from automated clients (#18857): webdriver is only
  // true under automation (our e2e suite, scrapers), and crawlers executing
  // the SPA produced ~half of prod notices. Real visitors keep feeding the
  // same faults, so regressions stay visible. Subsumes batch-1's crawler-gated
  // "Not Found" rule (fault 128962442).
  if (navigator.webdriver === true || isCrawlerUserAgent(userAgent)) {
    return false;
  }

  // Ignore UnhandledPromiseRejectionWarning: Unspecified reason
  // These are transient errors that don't provide actionable information
  // https://docs.honeybadger.io/lib/javascript/guides/reducing-noise/#transient-unhandledpromiserejectionwarning-errors
  if (/UnhandledPromiseRejectionWarning: Unspecified reason/.test(message)) {
    return false;
  }

  // Ignore "UnhandledPromiseRejectionWarning" when no additional info is available
  // These are transient errors that don't provide actionable information
  if (/UnhandledPromiseRejectionWarning: {}/.test(message)) {
    return false;
  }

  // Ignore state machine transition errors - timing/race condition issues
  // These occur when state machine events are triggered in unexpected states
  // Usually from async operations in assignment flows that aren't critical
  // https://app.honeybadger.io/projects/121794/faults/121074677
  if (/UnhandledPromiseRejectionWarning:.*No transition: from \d+ event \d+/.test(message)) {
    return false;
  }

  // Expected when users navigate away mid-fetch. Wire form carries the "Error: "
  // class prefix that HB fault titles strip, so match both.
  if (/UnhandledPromiseRejectionWarning: (Error: )?Object Not Found Matching Id/.test(message)) {
    return false;
  }

  // Ignore API errors - these are handled by application error handling
  // We show user-friendly messages and don't need to track them in Honeybadger
  // https://github.com/albert-io/nexus/issues/13502
  if (message && isApiError(notice)) {
    return false;
  }

  // Ignore Mandark API error responses - backend errors already handled
  // These are server-side errors that show appropriate user messages
  // https://app.honeybadger.io/projects/121794/faults/121063145
  if (/Mandark API error response|Failed to enroll student using relationship/.test(message)) {
    return false;
  }

  // Ignore JSXGraph error
  // Known issue with the JSXGraph library when manipulating DOM elements
  // https://github.com/albert-io/nexus/issues/13401
  if (
    notice.name === 'TypeError' &&
    message.includes("Cannot read properties of null (reading 'insertBefore')")
  ) {
    return false;
  }

  // Ignore MathJax v2 loader errors — vendored third-party code we will not patch.
  // Object.loadTimeout in MathJax.js reads `.timeout` off an undefined loader entry
  // when an extension script load times out (e.g. slow connections).
  // https://app.honeybadger.io/projects/121794/faults/128974588
  if (
    message.includes("Cannot read properties of undefined (reading 'timeout')") &&
    notice.stack &&
    notice.stack.includes('vendor/mathjax')
  ) {
    return false;
  }

  // Ignore SyntaxError: Unexpected token '<'
  // Usually indicates HTML returned instead of expected JSON/JS (CDN issues, etc.)
  // These are typically network/infrastructure issues, not code bugs
  if (notice.name === 'SyntaxError' && message.includes("Unexpected token '<'")) {
    return false;
  }

  // Ignore generic syntax errors - typically from bots or network issues
  // These include "Unexpected end of input", "Unexpected token", etc.
  // Often from crawlers that don't properly execute JS or corrupted file transfers
  // https://app.honeybadger.io/projects/121794/faults/108517488
  if (
    notice.name === 'SyntaxError' &&
    (message.includes('Unexpected end of input') ||
      message.includes('Unexpected token') ||
      message.includes('has already been declared'))
  ) {
    return false;
  }

  // Ignore errors from third-party services

  // List of third-party domains/patterns to ignore
  const thirdPartyPatterns = [
    'js.jam.dev', // Jam.dev screen recording tool
    'asset.goguardian', // GoGuardian school monitoring software
    'google.com', // Google services (GTM/GA/etc)
    'chrome-extension:', // Browser extensions
    'leadbooster-chat.pipedrive.com' // Pipedrive chat widget
  ];

  // Check stack trace for third-party origins
  if (notice.stack) {
    const hasThirdPartyError = thirdPartyPatterns.some((pattern) => notice.stack.includes(pattern));
    if (hasThirdPartyError) {
      return false;
    }
  }

  // Check message for third-party origins as well
  if (message) {
    const hasThirdPartyError = thirdPartyPatterns.some((pattern) => message.includes(pattern));
    if (hasThirdPartyError) {
      return false;
    }
  }

  // Ignore Slate error - will be fixed when upgrading to React 18
  // Slate editor library has known issues with React 16
  // https://github.com/albert-io/nexus/issues/13713#issuecomment-3428391193
  if (message.includes('Cannot resolve a Slate point from DOM point')) {
    return false;
  }

  // Ignore iOS max call stack error — iOS WebKit recursive rendering issue.
  // Affects iPhone, iPad, and iPod across views (assignments, classes, dashboard).
  // Timeboxed investigation in #13786 could not reproduce. WebKit unwinds the
  // stack on `Maximum call stack size exceeded`, so backtraces arrive empty and
  // source maps cannot pinpoint the recursion site. Initial filter only caught
  // iPad; ~91% of recent notices on this fault now come from iPhone.
  // https://github.com/albert-io/nexus/issues/13786
  // https://github.com/albert-io/nexus/issues/17637
  // https://app.honeybadger.io/projects/121794/faults/121578983
  if (message.includes('Maximum call stack size exceeded') && isIos) {
    return false;
  }

  // Ignore expected AbortErrors
  // "signal is aborted without reason" is thrown when a fetch is cancelled via
  // AbortController with no explicit reason, which is normal when components unmount
  // or users navigate away during a pending request.
  // https://app.honeybadger.io/projects/121794/faults/129099808
  // Audio player AbortError - when users stop audio playback, the browser throws an
  // AbortError. This is expected behavior and not a bug.
  // https://github.com/albert-io/nexus/issues/13438#event-19591642041
  if (notice.name === 'AbortError' && message.includes('aborted')) {
    if (/signal is aborted without reason/.test(message)) {
      return false;
    }
    // "The operation was aborted." is the benign media abort raised when audio
    // playback is interrupted by the user or by navigation away from the page.
    // https://app.honeybadger.io/projects/121794/faults/129848276
    // https://app.honeybadger.io/projects/121794/faults/128966588
    if (/The operation was aborted\.?/.test(message)) {
      return false;
    }
    const hasAudioPlayer = !!global.document.getElementsByClassName('audio-player__button').length;
    return !hasAudioPlayer; // Only ignore if audio player is present
  }

  // Ignore terminated requests - network issues, not application errors
  // Occurs when users navigate away during pending requests
  // https://github.com/albert-io/nexus/issues/13714
  if (/Request has been terminated/.test(message)) {
    return false;
  }

  // Ignore timeout errors - network issues beyond application control
  // These occur when API requests exceed timeout thresholds
  // Usually caused by slow network conditions or server-side issues
  // https://app.honeybadger.io/projects/121794/faults/121066843
  // https://app.honeybadger.io/projects/121794/faults/123823242
  if (/Timeout of 60000ms exceeded|Request timeout after 30000ms/.test(message)) {
    return false;
  }

  // Ignore polling cancelled errors - expected behavior when users navigate away
  // Occurs when report polling is intentionally cancelled (e.g., user leaves page)
  // https://app.honeybadger.io/projects/121794/faults/125334411
  if (/Polling cancelled/.test(message)) {
    return false;
  }

  // Ignore generic failed-fetch network errors - issues beyond our control.
  // Low-level browser network errors with no stack trace, caused by the user's
  // network conditions, browser issues, or CDN problems. Each browser words the
  // same failed fetch differently: Safari/Chrome say "Load failed", Firefox says
  // "NetworkError when attempting to fetch resource."
  // https://app.honeybadger.io/projects/121794/faults/108434352
  if (
    /^UnhandledPromiseRejectionWarning: (TypeError: )?(Load failed|NetworkError when attempting to fetch resource\.?)$/.test(
      message
    )
  ) {
    return false;
  }

  // Ignore chunk loading errors - network issues, handled by application
  // These occur during deployments or network issues and are handled gracefully
  // https://github.com/albert-io/nexus/issues/14032
  if (/Loading (CSS )?chunk [0-9]+ failed/.test(message)) {
    return false;
  }

  // Ignore "can't find variable: gmo" error
  // Third-party script issue that we can't control
  if (/Can't find variable: gmo/.test(message)) {
    return false;
  }

  // Ignore browser-injected variable errors - not our application code
  // __gCrWeb is injected by Chrome/Brave iOS, __firefox__ by Firefox mobile
  // $ errors from browser extensions that expect jQuery
  // https://app.honeybadger.io/projects/121794/faults/108702440
  // https://app.honeybadger.io/projects/121794/faults/111439525
  if (
    /Can't find variable: __gCrWeb|Can't find variable: __firefox__/.test(message) ||
    (message.includes('$ is not defined') &&
      notice.stack &&
      notice.stack.includes('chrome-extension:'))
  ) {
    return false;
  }

  // Ignore ResizeObserver errors - benign browser performance warnings
  // This warning occurs when ResizeObserver callbacks take too long to complete
  // It's not an actionable application bug and doesn't affect functionality
  // Chrome 84+ emits "loop completed with undelivered notifications";
  // older Chromium-based browsers emit "loop limit exceeded".
  // https://app.honeybadger.io/projects/121794/faults/109764248
  // https://app.honeybadger.io/projects/121794/faults/130477333
  if (
    /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/.test(message)
  ) {
    return false;
  }

  // Ignore "Failed to execute 'insertBefore' on 'Node'" errors
  // We are assuming that these errors are caused by in-browser translation tools.
  // See: https://github.com/albert-io/nexus/issues/15020#issuecomment-3647360462

  // Additionally, if we think this ignore is too broad we could try to detect whether or not in-browser translation has been used
  // and selectively ignore this error.
  if (
    /Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node./.test(
      message
    )
  ) {
    return false;
  }

  // Ignore "Failed to execute 'removeChild' on 'Node'" errors
  // Caused by Google Translate manipulating the DOM on chrome books
  // See: https://github.com/albert-io/nexus/issues/14283#issuecomment-3665651331
  if (
    /Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node./.test(
      message
    )
  ) {
    return false;
  }

  // Ignore GraphQL FeatureFlag query errors
  // https://app.honeybadger.io/projects/121794/faults/128962194
  // https://app.honeybadger.io/projects/121794/faults/128962193
  if (/GraphQL (network error|error) in FeatureFlag/.test(message)) {
    return false;
  }

  // Ignore GraphQL errors where the error detail is "undefined"
  // "undefined" as the error detail indicates a network/connection-level failure where
  // the GraphQL error payload could not be parsed — not an application logic bug.
  // Observed in StudentAssignments, ToDoSkills, and other queries on iPad/low-connectivity.
  // https://app.honeybadger.io/projects/121794/faults/128962731
  // https://app.honeybadger.io/projects/121794/faults/128971224
  if (/GraphQL error in \w+: undefined/.test(message)) {
    return false;
  }

  // Expected auth expiration, handled by the login redirect. Class prefix widened
  // to the wire form; anchored so longer messages still report.
  // https://app.honeybadger.io/projects/121794/faults/131811156
  if (/^UnhandledPromiseRejectionWarning: (\w*Error: )?Unauthorized$/.test(message)) {
    return false;
  }

  // Pure connectivity failure, surfaced to users via Apollo error states. Class
  // prefix widened to the wire form.
  // https://app.honeybadger.io/projects/121794/faults/123500744
  if (
    notice.name === 'ApolloError' &&
    /^(UnhandledPromiseRejectionWarning: (ApolloError: )?)?Failed to fetch$/.test(message)
  ) {
    return false;
  }

  // Ignore GraphQL "forbidden" errors — backend permission/authorization denials.
  // Thrown when a user requests a resource their role/license does not permit
  // (e.g. a student querying another student's assignments). The app surfaces a
  // normal "not permitted" state; there is no code defect to track.
  // https://app.honeybadger.io/projects/121794/faults/128962731
  // https://app.honeybadger.io/projects/121794/faults/129008269
  if (/GraphQL (network error|error) in \w+: forbidden/.test(message)) {
    return false;
  }

  // Ignore GraphQL/Apollo 401 responses — expected session expiration.
  // When a user's session expires, in-flight GraphQL requests return a 401. The
  // app's auth flow handles the redirect to login. The existing ApolloError filter
  // only matches "Failed to fetch", so these 401s leak through.
  // NOTE: Honeybadger fault titles strip the leading "Error: "/"ApolloError: " class
  // prefix that String(error) prepends in raw unhandledrejection payloads. Any
  // UnhandledPromiseRejectionWarning filter authored from a fault title must allow
  // that optional infix, and beforeNotify never mutates message, so coverage must be
  // tested against raw notice messages.
  // https://app.honeybadger.io/projects/121794/faults/128973415
  // https://app.honeybadger.io/projects/121794/faults/128962499
  // https://app.honeybadger.io/projects/121794/faults/131813341
  if (
    /GraphQL network error in \w+: Response not successful: Received status code 401/.test(
      message
    ) ||
    (notice.name === 'ApolloError' &&
      /^(UnhandledPromiseRejectionWarning: (ApolloError: )?)?Response not successful: Received status code 401$/.test(
        message
      ))
  ) {
    return false;
  }

  // Ignore "Invalid enrollment code" rejections — expected user error.
  // A student typed a wrong or expired class enrollment code. The error is shown to
  // the user inline; it is not an application defect. Raw rejection payloads carry
  // the "Error: " class prefix that fault titles strip (see the class-prefix note on
  // the 401 filter above); anchored so a longer message that merely starts with this
  // literal still reports.
  // https://app.honeybadger.io/projects/121794/faults/129404706
  // https://app.honeybadger.io/projects/121794/faults/130633651
  // https://app.honeybadger.io/projects/121794/faults/130630365
  if (/^UnhandledPromiseRejectionWarning: (Error: )?Invalid enrollment code$/.test(message)) {
    return false;
  }

  // Ignore "Google authentication failed" — expected OAuth flow failures.
  // Thrown by GoogleProvider when the user cancels the popup, the popup is blocked,
  // or third-party cookies are disabled. An expected auth-flow outcome, not a bug.
  // https://app.honeybadger.io/projects/121794/faults/129423021
  if (/Google authentication failed/.test(message)) {
    return false;
  }

  // Ignore canvas "Source image is unreachable" — image XHR/load failures (asset
  // reachability or CORS), thrown from the low-level request layer. Not a logic bug.
  // https://app.honeybadger.io/projects/121794/faults/131811156
  if (/Source image is unreachable/.test(message)) {
    return false;
  }

  // Ignore audio media-capability errors — environmental, browser/device dependent.
  // "Audio format not supported" is the browser rejecting the media MIME/codec; the
  // play/pause race fires when playback is interrupted. Neither is an app defect.
  // (The related audio AbortError is handled in the AbortError block above.)
  // https://app.honeybadger.io/projects/121794/faults/129008582
  // https://app.honeybadger.io/projects/121794/faults/129851559
  if (
    /Audio format not supported/.test(message) ||
    /The play\(\) request was interrupted by a call to pause\(\)/.test(message)
  ) {
    return false;
  }

  // Ignore blocked-storage errors — environmental (iOS Safari private mode, disabled
  // cookies / third-party storage). The app degrades gracefully when storage is denied.
  // https://app.honeybadger.io/projects/121794/faults/128967312
  // https://app.honeybadger.io/projects/121794/faults/128991373
  if (
    (notice.name === 'SecurityError' && /The operation is insecure\.?/.test(message)) ||
    /Failed to read the 'localStorage' property from 'Window'/.test(message)
  ) {
    return false;
  }

  // Ignore automation / anti-bot probing and injected third-party scripts — not our code.
  // "Cannot redefine property: webdriver" is thrown by automation frameworks and anti-bot
  // scripts redefining navigator.webdriver. The invalid-selector probe comes from scripts
  // injected via anonymous eval (Google one-tap: Referer accounts.google.com, backtrace
  // "<anonymous>") querying for "div:has(> iframe[id='…'])". Matched on that specific
  // injected signature so genuine invalid-selector bugs in our own code still report.
  // https://app.honeybadger.io/projects/121794/faults/128964427
  // https://app.honeybadger.io/projects/121794/faults/130198991
  if (
    /Cannot redefine property: webdriver/.test(message) ||
    (/:has\(>\s*iframe\[id=/.test(message) && /is not a valid selector/.test(message))
  ) {
    return false;
  }

  // Ignore third-party Intercom widget crashes — code we do not own.
  // Stack originates entirely in widget.intercom.io. The message alone is too generic
  // to match safely, so this is gated on the stack origin.
  // https://app.honeybadger.io/projects/121794/faults/130730768
  if (notice.stack && notice.stack.includes('widget.intercom.io')) {
    return false;
  }

  // Report all other errors
  return true;
};
